import axios from 'axios';

// Lazy imports to break circular dependency: store → slices → api → store
let _store = null;
let _updateTokens = null;
const getStore = () => {
  if (!_store) {
    _store = require('../store').default;
    _updateTokens = require('../store/slices/authSlice').updateTokens;
  }
  return _store;
};

const baseURL = process.env.REACT_APP_API_BASE || '';
// F-23: timeout 30초로 증가 (대량검색/업로드 지원)
const api = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true, // httpOnly 쿠키 자동 전송
});

// Request interceptor: 쿠키 + Authorization 헤더 이중 전송 (하위 호환)
// 새 백엔드: 쿠키 우선 사용, 구 백엔드: Authorization 헤더 사용
api.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.accessToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${parsed.accessToken}`;
      }
    }
  } catch (_) {}
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for 401 responses, not on refresh/login endpoints themselves
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/users/refresh') ||
      originalRequest.url?.includes('/users/login')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // 리프레시: 쿠키 자동 전송 + body에도 토큰 포함 (하위 호환)
      const raw = localStorage.getItem('userInfo');
      const parsed = raw ? JSON.parse(raw) : {};
      const body = parsed?.refreshToken ? { refreshToken: parsed.refreshToken } : {};

      const { data } = await axios.post(
        '/api/users/refresh',
        body,
        { withCredentials: true, timeout: 10000 }
      );

      // localStorage + Redux 동기화
      const updated = {
        _id: data._id,
        username: data.username,
        role: data.role,
        ...(data.accessToken && { accessToken: data.accessToken }),
        ...(data.refreshToken && { refreshToken: data.refreshToken }),
      };
      localStorage.setItem('userInfo', JSON.stringify(updated));
      getStore().dispatch(_updateTokens(updated));

      // 새 토큰으로 원래 요청 재시도
      if (data.accessToken) {
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
      }
      processQueue(null, data.accessToken);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed — clear auth state and redirect to login
      localStorage.removeItem('userInfo');
      localStorage.removeItem('loginTime');
      window.location.href = '/nklogin';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
